import { queueMacrotask } from 'module/helpers/macrotask';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { Button, type ButtonProps } from '@rocketlane/ui';
import { Tooltip } from 'antd';
import { ButtonType } from 'antd/lib/button';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { InputProps } from 'antd/lib/input';
import {
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { ifProp, prop, switchProp } from 'styled-tools';
import { Height, HeightStyle, InputFocus, InputStyle, Input } from './input';
import { FontAwesomeIcon } from '../icon/icon';
import { IconWrapper } from '../icon-shell/icon-shell';

type ExpandDirection = 'left' | 'right';

const getButtonVariant = (type: ButtonType): ButtonProps['variant'] => {
  switch (type) {
    case 'primary':
      return 'primary';
    case 'link':
      return 'link';
    case 'text':
    case 'ghost':
      return 'ghost';
    default:
      return 'secondary';
  }
};

const getButtonSize = (size: SizeType): ButtonProps['size'] => {
  switch (size) {
    case 'small':
      return 'small';
    case 'large':
      return 'large';
    default:
      return 'medium';
  }
};

const SearchButton = styled(Button).attrs({
  type: 'button',
  leftIcon: (
    <IconWrapper>
      <FontAwesomeIcon
        icon={faSearch}
        size={16}
        iconSize={12}
        color="transparent"
      />
    </IconWrapper>
  ),
})``;
const CloseButton = styled(Button).attrs({
  type: 'button',
  leftIcon: (
    <IconWrapper>
      <FontAwesomeIcon
        icon={faXmark}
        size={16}
        iconSize={12}
        color="transparent"
      />
    </IconWrapper>
  ),
})``;
const Wrapper = styled.div<{ active: boolean; size: SizeType }>`
  display: flex;
  align-items: center;
  transition: all 0.11s cubic-bezier(0.2, 0, 0.38, 0.9);
  max-width: ${HeightStyle};
  overflow: hidden;

  ${ifProp(
    'active',
    css`
      ${InputStyle};
      max-width: 100%;
      padding: 0;
      &:focus-within {
        ${InputFocus};
      }
      ${SearchButton} {
        pointer-events: none;
      }
    `,
  )};

  ${Input} {
    padding: 0;
    height: calc(${HeightStyle} - 6px);
    border: none;
    outline: none;
  }
`;
const AnimationWrapper = styled.div<{
  active: boolean;
  size: SizeType;
  direction: ExpandDirection;
  minWidth?: string;
  width?: string | number;
}>`
  position: relative;
  min-width: ${(props) => props.minWidth ?? '180px'};
  width: ${(props) => props.width ?? '100%'};
  ${Height};
  ${Wrapper} {
    position: absolute;
    ${switchProp(prop('direction', 'left'), {
      left: css`
        left: 0px;
      `,
      right: css`
        right: 0px;
      `,
    })};
  }
`;

type SearchInputProps = InputProps & {
  direction?: ExpandDirection;
  tooltip?: ReactNode;
  searchButtonProps?: {
    type: ButtonType;
    disabled?: boolean;
  };
  minWidth?: string;
  onFocusBeforeActive?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClose?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export type ExpandableSearchInputRef = {
  openSearchInput: VoidFunction;
  closeSearchInput: VoidFunction;
};

export const ExpandableSearchInput = forwardRef<
  ExpandableSearchInputRef,
  SearchInputProps
>(
  (
    {
      onChange,
      onBlur,
      onFocus,
      onFocusBeforeActive,
      onClose,
      size = 'middle',
      direction = 'left',
      className,
      tooltip,
      searchButtonProps = { type: 'default' },
      minWidth,
      width,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<any>(null);
    const [active, setActive] = useState(false);
    const [value, setValue] = useState('');

    const openSearchInput = useCallback(() => {
      setActive(true);
      queueMacrotask(() => {
        inputRef.current?.focus();
      });
    }, []);

    const onChangeValue = useCallback(
      (e) => {
        setValue(e.target.value);
        onChange?.(e);
      },
      [onChange],
    );

    const closeSearchInput = useCallback(() => {
      setActive(false);
      const event = Object.create({});
      const target = inputRef.current.input;
      const currentTarget = target.cloneNode(true) as HTMLInputElement;

      event.target = currentTarget;
      event.currentTarget = currentTarget;
      currentTarget.value = '';

      onChangeValue(event as React.ChangeEvent<HTMLInputElement>);
      onClose?.(event as React.ChangeEvent<HTMLInputElement>);
    }, [onChangeValue, onClose]);

    useImperativeHandle(
      ref,
      () => ({
        openSearchInput,
        closeSearchInput,
      }),
      [openSearchInput, closeSearchInput],
    );

    return (
      <AnimationWrapper
        active={active}
        size={size}
        direction={direction}
        className={className}
        minWidth={minWidth}
        width={width}
      >
        <Wrapper active={active} size={size} className="search-input-wrapper">
          <Tooltip title={tooltip}>
            <SearchButton
              size={getButtonSize(size)}
              variant={getButtonVariant(
                active ? 'text' : searchButtonProps.type,
              )}
              disabled={searchButtonProps?.disabled ?? false}
              tabIndex={active ? -1 : 0}
              onFocus={openSearchInput}
              onClick={openSearchInput}
              data-cy="search-input.button"
            />
          </Tooltip>
          <Input
            {...props}
            tabIndex={active ? 0 : -1}
            ref={inputRef}
            value={value}
            onChange={onChangeValue}
            onFocus={(e) => {
              onFocusBeforeActive?.(e);
              setActive(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              if (!e.target.value) {
                setActive(false);
              }
              onBlur?.(e);
            }}
            data-cy="search.text-field"
          />
          <CloseButton
            data-cy="search-input.close-button"
            variant="ghost"
            size={getButtonSize(size)}
            tabIndex={-1}
            onClick={closeSearchInput}
          />
        </Wrapper>
      </AnimationWrapper>
    );
  },
);

ExpandableSearchInput.displayName = 'ExpandableSearchInput';
